import { useApiQuery } from '../useApiQuery';
import { bookingKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import { mapAppointmentStatus } from './useBookings';
import type { Booking, BookingAddress } from '@/types/booking';

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function addressFromLine(addressLine: string, zipCode: string): BookingAddress {
  const parts = addressLine
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return {
      street: parts.slice(0, -2).join(', '),
      city: parts.at(-2),
      state: parts.at(-1),
      zip: zipCode || undefined,
      country: 'US',
    };
  }

  if (parts.length === 2) {
    return {
      street: parts[0],
      city: parts[1],
      zip: zipCode || undefined,
      country: 'US',
    };
  }

  return {
    street: addressLine,
    zip: zipCode && !addressLine.includes(zipCode) ? zipCode : undefined,
    country: 'US',
  };
}

function bookingAddressFromRaw(b: Record<string, unknown>): BookingAddress | undefined {
  if (b.address && typeof b.address === 'object') return b.address as BookingAddress;
  if (b.serviceAddress && typeof b.serviceAddress === 'object') {
    return b.serviceAddress as BookingAddress;
  }

  const addressLine = stringValue(b.address);
  if (!addressLine) return undefined;

  return addressFromLine(addressLine, stringValue(b.zipCode ?? b.zip));
}

export function transformBooking(b: Record<string, unknown>): Booking {
  const id = String(b.bookingId ?? b.id ?? '');
  const rawStatus = String(b.status ?? '');
  const hasSchedule = Boolean(b.startAt ?? b.scheduledStart ?? b.scheduledDate);
  const status =
    !hasSchedule && rawStatus === 'RESERVED' ? 'unscheduled' : mapAppointmentStatus(rawStatus);
  const serviceType = b.serviceType as
    | { id?: string; name?: string; slug?: string }
    | null
    | undefined;
  const serviceName = (serviceType?.name ?? String(b.serviceName ?? '')) || undefined;
  const jobType = b.jobType as
    | { id?: string; name?: string; color?: string; icon?: string | null }
    | null
    | undefined;

  const customerName = String(b.customerName ?? '');
  const contactId = stringValue(b.customerId ?? b.contactId);
  const nameParts = customerName.trim().split(' ');
  const contact = customerName
    ? {
        id: contactId,
        firstName: nameParts[0] ?? customerName,
        lastName: nameParts.slice(1).join(' ') || undefined,
        phone: String(b.phone ?? '') || undefined,
        email: String(b.email ?? b.customerEmail ?? '') || undefined,
        company: String(b.company ?? b.customerCompany ?? '') || undefined,
      }
    : undefined;

  const technicianNames = (b.technicianNames as string[] | undefined) ?? [];
  const technicianIds = (b.technicianIds as string[] | undefined) ?? [];
  const rawTechnicians = Array.isArray(b.technicians)
    ? (b.technicians as Array<Record<string, unknown>>)
    : [];
  const technicians =
    rawTechnicians.length > 0
      ? rawTechnicians.map((tech) => {
          const name = String(tech.name ?? '').trim();
          const parts = name.split(' ');
          return {
            id: String(tech.id ?? ''),
            firstName: parts[0] ?? name,
            lastName: parts.slice(1).join(' ') || undefined,
            email: String(tech.email ?? '') || undefined,
            phone: String(tech.phone ?? '') || undefined,
            role: tech.role === 'LEAD' ? 'LEAD' : 'HELPER',
          };
        })
      : technicianNames.length > 0
        ? technicianNames.map((name, i) => {
            const parts = name.split(' ');
            return {
              id: technicianIds[i] ?? '',
              firstName: parts[0] ?? name,
              lastName: parts.slice(1).join(' ') || undefined,
              role: i === 0 ? ('LEAD' as const) : ('HELPER' as const),
            };
          })
        : b.technicianName
          ? [
              {
                id: String(b.technicianId ?? ''),
                firstName: (b.technicianName as string).split(' ')[0] ?? '',
                lastName: (b.technicianName as string).split(' ').slice(1).join(' ') || undefined,
                role: 'LEAD' as const,
              },
            ]
          : undefined;

  return {
    id,
    requestNumber: String(b.requestNumber ?? ''),
    jobId: typeof b.jobId === 'string' ? b.jobId : null,
    irisBriefing: typeof b.irisBriefing === 'string' ? b.irisBriefing : null,
    irisConversation: Array.isArray(b.irisConversation)
      ? (b.irisConversation as Array<{ role: string; body: string; at?: string }>)
      : null,
    irisSurveyAnswers: Array.isArray(b.irisSurveyAnswers)
      ? (b.irisSurveyAnswers as Array<{
          questionId: string;
          question: string;
          answer: string;
          mediaUrls?: string[];
        }>)
      : null,
    // Equipment captured at intake — buildJobFromBookingDto forwards it, so
    // dropping it here would silently lose the units on "Create job".
    equipmentIds: Array.isArray(b.equipmentIds)
      ? (b.equipmentIds as unknown[]).filter(
          (id): id is string => typeof id === 'string' && id.trim().length > 0,
        )
      : null,
    // The saved ContactAddress this booking was placed at — the manual
    // create-job path forwards it as serviceAddress.addressId, so dropping it
    // here would silently put that path back on fuzzy street matching.
    serviceAddressId: typeof b.serviceAddressId === 'string' ? b.serviceAddressId : null,
    appointmentId:
      typeof b.appointmentId === 'string' && b.appointmentId.trim() ? b.appointmentId.trim() : null,
    appointmentNumber: typeof b.appointmentNumber === 'number' ? b.appointmentNumber : null,
    contactId: contactId || undefined,
    status,
    serviceName,
    serviceType: serviceType?.id
      ? {
          id: serviceType.id,
          name: serviceType.name ?? serviceName ?? '',
          slug: serviceType.slug,
        }
      : undefined,
    jobType: jobType?.id
      ? {
          id: jobType.id,
          name: jobType.name ?? '',
          color: jobType.color ?? '#2196F3',
          icon: jobType.icon ?? null,
        }
      : null,
    title: jobType?.name ?? serviceName,
    scheduledStart: String(b.startAt ?? '') || undefined,
    scheduledEnd: String(b.endAt ?? '') || undefined,
    scheduledDate: String(b.scheduledDate ?? '') || undefined,
    granularity:
      b.granularity === 'TIMED' || b.granularity === 'DAY_FLEXIBLE' || b.granularity === 'ALL_DAY'
        ? b.granularity
        : undefined,
    timeCommitted: typeof b.timeCommitted === 'boolean' ? b.timeCommitted : undefined,
    timezone: String(b.timezone ?? '') || undefined,
    siteTimezone: String(b.siteTimezone ?? '') || undefined,
    duration: Number.isFinite(Number(b.durationMinutes ?? b.duration))
      ? Number(b.durationMinutes ?? b.duration)
      : undefined,
    address: bookingAddressFromRaw(b),
    contact,
    technicians,
    checklist: Array.isArray(b.checklist) ? (b.checklist as never) : undefined,
    attachments: Array.isArray(b.attachments) ? (b.attachments as never) : undefined,
    quoteId: typeof b.quoteId === 'string' ? b.quoteId : undefined,
    invoiceId: typeof b.invoiceId === 'string' ? b.invoiceId : undefined,
    invoicePaid: typeof b.invoicePaid === 'boolean' ? b.invoicePaid : undefined,
    issueSummary: String(b.issueSummary ?? '') || undefined,
    internalNotes: String(b.notes ?? '') || undefined,
    customerNotes: String(b.customerNotes ?? '') || undefined,
    sourceCallControlId:
      typeof b.sourceCallControlId === 'string' && b.sourceCallControlId.trim()
        ? b.sourceCallControlId.trim()
        : undefined,
    createdAt: String(b.createdAt ?? ''),
    updatedAt: String(b.updatedAt ?? ''),
  } as Booking;
}

export function useBooking(id: string) {
  return useApiQuery<Booking>({
    queryKey: bookingKeys.detail(id),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        schedulerEndpoints.booking(id),
      );
      return transformBooking(raw);
    },
    enabled: Boolean(id),
    showErrorToast: false,
  });
}
